import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Briefcase, 
  Clock, 
  GraduationCap, 
  Heart, 
  Lightbulb, 
  MessageCircle, 
  Target, 
  TrendingUp, 
  Users, 
  Award,
  ChevronRight,
  BookOpen,
  Video,
  FileText,
  Calendar,
  Star,
  CheckCircle2,
  Baby,
  Home as HomeIcon,
  UserCircle,
  HelpCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Progress } from './ui/progress';

export function ReturnerHub() {
  const [selectedPath, setSelectedPath] = useState<'parent' | 'carer' | 'unemployed' | null>(null);

  const paths = [
    {
      id: 'parent' as const,
      title: 'Returning Parents',
      description: 'Resources and support for parents returning to work after parental leave',
      icon: Baby,
      color: 'from-pink-400 to-purple-500',
      stats: { jobs: 3200, success: 92 }
    },
    {
      id: 'carer' as const,
      title: 'Returning Carers',
      description: 'Flexible opportunities for those returning after caring responsibilities',
      icon: Heart,
      color: 'from-blue-400 to-cyan-500',
      stats: { jobs: 2800, success: 89 }
    },
    {
      id: 'unemployed' as const,
      title: 'Long-Term Unemployed',
      description: 'Pathways back to employment with training and support',
      icon: TrendingUp,
      color: 'from-[#f7ef8a] to-orange-500',
      stats: { jobs: 4500, success: 85 }
    }
  ];

  const returnerFriendlyJobs = [
    {
      id: 1,
      title: 'Customer Service Representative',
      company: 'TechCorp Solutions',
      location: 'Remote',
      type: 'Part-time',
      salary: '£22,000 - £28,000',
      tags: ['Returner Program', 'Flexible Hours', 'Training Provided'],
      match: 95
    },
    {
      id: 2,
      title: 'Junior Accountant',
      company: 'Finance Plus Ltd',
      location: 'London (Hybrid)',
      type: 'Full-time',
      salary: '£28,000 - £35,000',
      tags: ['Career Break Friendly', 'Refresher Training', 'Mentorship'],
      match: 88
    },
    {
      id: 3,
      title: 'Marketing Coordinator',
      company: 'Creative Studios',
      location: 'Manchester',
      type: 'Part-time',
      salary: '£25,000 - £30,000',
      tags: ['Job Share Available', 'Flexible Start Date', 'School Hours'],
      match: 92
    },
    {
      id: 4,
      title: 'Administrative Assistant',
      company: 'Healthcare Group',
      location: 'Birmingham',
      type: 'Full-time',
      salary: '£20,000 - £24,000',
      tags: ['No Experience Gap Penalty', 'On-the-job Training'],
      match: 86
    }
  ];

  const trainingPrograms = [
    {
      title: 'Digital Skills Bootcamp',
      provider: 'Tech Academy',
      duration: '12 weeks',
      format: 'Online',
      cost: 'Free',
      description: 'Learn essential digital skills including Microsoft Office, Google Suite, and basic coding',
      nextStart: '15 Nov 2025',
      spots: 8
    },
    {
      title: 'Professional Confidence Workshop',
      provider: 'Career Builders',
      duration: '4 weeks',
      format: 'Hybrid',
      cost: 'Free',
      description: 'Build confidence, update interview skills, and network with other returners',
      nextStart: '22 Nov 2025',
      spots: 12
    },
    {
      title: 'Industry Re-entry Program',
      provider: 'Finance Leaders',
      duration: '6 weeks',
      format: 'In-person',
      cost: '£299',
      description: 'Sector-specific training for finance professionals returning after career breaks',
      nextStart: '1 Dec 2025',
      spots: 5
    },
    {
      title: 'Flexible Working Masterclass',
      provider: 'Work-Life Balance Institute',
      duration: '2 days',
      format: 'Online',
      cost: 'Free',
      description: 'Learn strategies to negotiate flexible working arrangements and manage work-life balance',
      nextStart: '10 Nov 2025',
      spots: 20
    }
  ];

  const successStories = [
    {
      name: 'Sarah Johnson',
      role: 'Marketing Manager',
      company: 'Digital Agency Ltd',
      breakDuration: '3 years',
      story: 'After three years focusing on my young children, I was nervous about returning. The returner program gave me the confidence and updated skills I needed. Now I\'m thriving in a flexible role that works for my family.',
      image: 'SJ'
    },
    {
      name: 'Michael Chen',
      role: 'Software Developer',
      company: 'TechStart Inc',
      breakDuration: '5 years',
      story: 'Caring for my elderly parents meant stepping away from tech. The refresher courses and mentorship program helped me get back up to speed. I\'m now working on exciting projects with a supportive team.',
      image: 'MC'
    },
    {
      name: 'Emma Williams',
      role: 'HR Coordinator',
      company: 'People First HR',
      breakDuration: '2 years',
      story: 'The career gap felt daunting, but employers on this platform truly value diverse experiences. I found a role that appreciates my transferable skills and offers the flexibility I need.',
      image: 'EW'
    }
  ];

  const resources = [
    {
      category: 'CV & Applications',
      icon: FileText,
      items: [
        { title: 'How to Explain Career Gaps', type: 'Guide', duration: '5 min read' },
        { title: 'CV Templates for Returners', type: 'Template', duration: 'Download' },
        { title: 'Writing a Compelling Cover Letter', type: 'Video', duration: '12 min' },
        { title: 'Highlighting Transferable Skills', type: 'Workshop', duration: '45 min' }
      ]
    },
    {
      category: 'Interview Preparation',
      icon: MessageCircle,
      items: [
        { title: 'Common Interview Questions for Returners', type: 'Guide', duration: '8 min read' },
        { title: 'Virtual Interview Best Practices', type: 'Video', duration: '15 min' },
        { title: 'Mock Interview Practice', type: 'Interactive', duration: '30 min' },
        { title: 'Salary Negotiation Tips', type: 'Guide', duration: '6 min read' }
      ]
    },
    {
      category: 'Skills Development',
      icon: GraduationCap,
      items: [
        { title: 'Free Online Courses Directory', type: 'Resource', duration: 'Browse' },
        { title: 'Industry Certifications Guide', type: 'Guide', duration: '10 min read' },
        { title: 'Networking for Returners', type: 'Webinar', duration: '1 hour' },
        { title: 'Personal Branding Workshop', type: 'Workshop', duration: '2 hours' }
      ]
    },
    {
      category: 'Work-Life Balance',
      icon: Clock,
      items: [
        { title: 'Flexible Working Rights', type: 'Guide', duration: '7 min read' },
        { title: 'Childcare Support Options', type: 'Resource', duration: 'Browse' },
        { title: 'Managing Career and Caring', type: 'Video', duration: '20 min' },
        { title: 'Time Management Strategies', type: 'Workshop', duration: '1 hour' }
      ]
    }
  ];

  const mentorshipProgram = {
    title: 'Returner Mentorship Program',
    description: 'Connect with experienced professionals who have successfully returned to work after career breaks',
    benefits: [
      'One-on-one guidance from someone who understands your journey',
      'Industry insights and networking opportunities',
      'Confidence building and career planning support',
      'Monthly group meetups with other returners'
    ],
    stats: {
      mentors: 150,
      matches: 89,
      satisfaction: 96
    }
  };

  if (selectedPath) {
    return (
      <div className="relative min-h-screen pt-16 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => setSelectedPath(null)}
            className="mb-8 text-gray-300 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Returner Hub
          </Button>

          {/* Path-specific content would go here */}
          <div className="text-center py-20">
            <h2 className="mb-4 text-gold-gradient">
              {paths.find(p => p.id === selectedPath)?.title}
            </h2>
            <p className="text-gray-300">Tailored resources and opportunities coming soon...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen pt-16 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]" />
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#edc967]/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#ba9036]/10 border border-[#d2ac47]/20 rounded-full px-4 py-2 mb-6">
              <Heart className="w-4 h-4 text-[#ba9036]" />
              <span className="text-sm text-[#ba9036]">Welcome Back to Your Career</span>
            </div>
            
            <h1 className="mb-6 text-gold-gradient">
              Returner Hub
            </h1>
            <p className="text-xl text-gray-300 mb-12">
              Dedicated support for parents, carers, and long-term unemployed individuals returning to work. 
              Your career break is a strength, not a setback.
            </p>

            {/* Path Selection Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {paths.map((path) => {
                const Icon = path.icon;
                return (
                  <Card
                    key={path.id}
                    className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300 cursor-pointer group"
                    onClick={() => setSelectedPath(path.id)}
                  >
                    <CardContent className="p-6 text-center">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${path.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="mb-2 text-white">{path.title}</h3>
                      <p className="text-sm text-gray-400 mb-4">{path.description}</p>
                      <div className="flex justify-around text-sm">
                        <div>
                          <div className="text-[#ba9036]">{path.stats.jobs}+</div>
                          <div className="text-gray-500">Jobs</div>
                        </div>
                        <div>
                          <div className="text-[#ba9036]">{path.stats.success}%</div>
                          <div className="text-gray-500">Success</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Tabs */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="jobs" className="w-full">
            <TabsList className="grid w-full grid-cols-5 bg-white/5 border border-white/10 mb-8">
              <TabsTrigger value="jobs" className="data-[state=active]:bg-[#ba9036]/20 data-[state=active]:text-[#ba9036]">
                <Briefcase className="w-4 h-4 mr-2" />
                Jobs
              </TabsTrigger>
              <TabsTrigger value="training" className="data-[state=active]:bg-[#ba9036]/20 data-[state=active]:text-[#ba9036]">
                <GraduationCap className="w-4 h-4 mr-2" />
                Training
              </TabsTrigger>
              <TabsTrigger value="resources" className="data-[state=active]:bg-[#ba9036]/20 data-[state=active]:text-[#ba9036]">
                <BookOpen className="w-4 h-4 mr-2" />
                Resources
              </TabsTrigger>
              <TabsTrigger value="mentorship" className="data-[state=active]:bg-[#ba9036]/20 data-[state=active]:text-[#ba9036]">
                <Users className="w-4 h-4 mr-2" />
                Mentorship
              </TabsTrigger>
              <TabsTrigger value="stories" className="data-[state=active]:bg-[#ba9036]/20 data-[state=active]:text-[#ba9036]">
                <Star className="w-4 h-4 mr-2" />
                Stories
              </TabsTrigger>
            </TabsList>

            {/* Returner-Friendly Jobs */}
            <TabsContent value="jobs" className="space-y-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="mb-2 text-gold-gradient">
                    Returner-Friendly Jobs
                  </h2>
                  <p className="text-gray-400">Employers who value career breaks and offer flexible arrangements</p>
                </div>
                <Button asChild className="bg-gold-gradient hover:brightness-110 text-black">
                  <Link to="/candidate">
                    View All Jobs
                  </Link>
                </Button>
              </div>

              <div className="grid gap-4">
                {returnerFriendlyJobs.map((job) => (
                  <Card
                    key={job.id}
                    className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300 group"
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-white">{job.title}</h3>
                            <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/30">
                              {job.match}% Match
                            </Badge>
                          </div>
                          <p className="text-gray-400 mb-2">{job.company}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <HomeIcon className="w-4 h-4" />
                              {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {job.type}
                            </span>
                            <span className="flex items-center gap-1">
                              <Target className="w-4 h-4" />
                              {job.salary}
                            </span>
                          </div>
                        </div>
                        <Button variant="outline" className="border-[#d2ac47]/30 text-[#ba9036] hover:bg-[#ba9036]/10">
                          Apply Now
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {job.tags.map((tag, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            className="border-white/20 text-gray-300"
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1 text-green-400" />
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Training Programs */}
            <TabsContent value="training" className="space-y-6">
              <div className="mb-6">
                <h2 className="mb-2 text-gold-gradient">
                  Training & Upskilling Programs
                </h2>
                <p className="text-gray-400">Refresh your skills and boost your confidence with specialized programs</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {trainingPrograms.map((program, index) => (
                  <Card
                    key={index}
                    className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
                  >
                    <CardHeader>
                      <div className="flex justify-between items-start mb-2">
                        <CardTitle className="text-white">{program.title}</CardTitle>
                        <Badge className={program.cost === 'Free' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'}>
                          {program.cost}
                        </Badge>
                      </div>
                      <CardDescription className="text-gray-400">{program.provider}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-gray-300">{program.description}</p>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-gray-500 mb-1">Duration</div>
                          <div className="text-white">{program.duration}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 mb-1">Format</div>
                          <div className="text-white">{program.format}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 mb-1">Spots Left</div>
                          <div className="text-[#ba9036]">{program.spots}</div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-white/10">
                        <div className="text-sm">
                          <span className="text-gray-500">Next start: </span>
                          <span className="text-white">{program.nextStart}</span>
                        </div>
                        <Button size="sm" className="bg-gold-gradient hover:brightness-110 text-black">
                          Enroll
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Resources */}
            <TabsContent value="resources" className="space-y-6">
              <div className="mb-6">
                <h2 className="mb-2 text-gold-gradient">
                  Career Resources & Guides
                </h2>
                <p className="text-gray-400">Everything you need to successfully return to work</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {resources.map((resource, index) => {
                  const Icon = resource.icon;
                  return (
                    <Card
                      key={index}
                      className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
                    >
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] flex items-center justify-center">
                            <Icon className="w-5 h-5 text-black" />
                          </div>
                          <CardTitle className="text-white">{resource.category}</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {resource.items.map((item, itemIndex) => (
                          <div
                            key={itemIndex}
                            className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer group"
                          >
                            <div className="flex-1">
                              <div className="text-white mb-1 group-hover:text-[#ba9036] transition-colors">
                                {item.title}
                              </div>
                              <div className="text-sm text-gray-500">
                                {item.type} • {item.duration}
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-[#ba9036] transition-colors" />
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            {/* Mentorship Program */}
            <TabsContent value="mentorship" className="space-y-6">
              <div className="mb-6">
                <h2 className="mb-2 text-gold-gradient">
                  {mentorshipProgram.title}
                </h2>
                <p className="text-gray-400">{mentorshipProgram.description}</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <Card className="bg-white/5 border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl mb-2 text-gold-gradient">
                      {mentorshipProgram.stats.mentors}+
                    </div>
                    <div className="text-gray-400">Active Mentors</div>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl mb-2 text-gold-gradient">
                      {mentorshipProgram.stats.matches}%
                    </div>
                    <div className="text-gray-400">Match Success Rate</div>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/10 text-center">
                  <CardContent className="p-6">
                    <div className="text-3xl mb-2 text-gold-gradient">
                      {mentorshipProgram.stats.satisfaction}%
                    </div>
                    <div className="text-gray-400">Satisfaction Score</div>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-gradient-to-br from-[#edc967]/10 to-transparent border-[#d2ac47]/20">
                <CardContent className="p-8">
                  <h3 className="mb-4 text-white">Program Benefits</h3>
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    {mentorshipProgram.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#ba9036] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{benefit}</span>
                      </div>
                    ))}
                  </div>
                  <Button className="bg-gold-gradient hover:brightness-110 text-black">
                    Apply for Mentorship
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Success Stories */}
            <TabsContent value="stories" className="space-y-6">
              <div className="mb-6">
                <h2 className="mb-2 text-gold-gradient">
                  Success Stories
                </h2>
                <p className="text-gray-400">Real stories from people who successfully returned to work</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {successStories.map((story, index) => (
                  <Card
                    key={index}
                    className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] flex items-center justify-center text-black">
                          {story.image}
                        </div>
                        <div>
                          <div className="text-white mb-1">{story.name}</div>
                          <div className="text-sm text-gray-400">{story.role}</div>
                          <div className="text-sm text-gray-500">{story.company}</div>
                        </div>
                      </div>
                      <Badge className="mb-4 bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/30">
                        {story.breakDuration} career break
                      </Badge>
                      <p className="text-gray-300 text-sm italic">"{story.story}"</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="bg-gradient-to-br from-[#edc967]/10 to-transparent border-[#d2ac47]/20 mt-8">
                <CardContent className="p-8 text-center">
                  <h3 className="mb-4 text-white">Share Your Story</h3>
                  <p className="text-gray-300 mb-6">
                    Have you successfully returned to work after a career break? Inspire others by sharing your journey.
                  </p>
                  <Button variant="outline" className="border-[#d2ac47]/30 text-[#ba9036] hover:bg-[#ba9036]/10">
                    Submit Your Story
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-transparent to-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-[#edc967]/20 via-[#edc967]/10 to-transparent border-[#d2ac47]/30 overflow-hidden">
            <CardContent className="p-12 text-center relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#f7ef8a]/5 to-transparent" />
              <div className="relative z-10">
                <h2 className="mb-4 text-gold-gradient">
                  Ready to Start Your Return Journey?
                </h2>
                <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                  Join thousands of successful returners who found their perfect role through Lighthouse Jobs
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    asChild
                    size="lg"
                    className="bg-gold-gradient hover:brightness-110 text-black"
                  >
                    <Link to="/candidate">
                      <UserCircle className="w-5 h-5 mr-2" />
                      Create Your Profile
                    </Link>
                  </Button>
                  <Button 
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-[#d2ac47]/30 text-[#ba9036] hover:bg-[#ba9036]/10"
                  >
                    <Link to="/help">
                      <HelpCircle className="w-5 h-5 mr-2" />
                      Get Support
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
